<template>
  <div class="inbox-container">
    <!-- Main layout -->
    <div class="inbox-layout">
      <!-- Main content area -->
      <div class="inbox-main">
        <!-- Header section -->
        <header class="inbox-header">
          <div class="header-content">
            <div class="header-info">
              <h1 class="header-title">{{ pageTitle }}</h1>
              <p v-if="currentEmail" class="header-subtitle">
                <span class="message-count">{{ totalConversations }}</span>
                {{
                  totalConversations === 1 ? "conversation" : "conversations"
                }}
              </p>
            </div>

            <div class="header-actions">
              <button
                @click="refreshConversations"
                class="action-button refresh-button"
                :disabled="refreshing"
                :class="{ 'is-refreshing': refreshing }"
              >
                <svg
                  class="button-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M23 4v6h-6"></path>
                  <path d="M1 20v-6h6"></path>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
                  <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"></path>
                </svg>
                <span>{{ refreshing ? "Refreshing..." : "Refresh" }}</span>
              </button>

              <button @click="goBack" class="action-button back-button">
                <svg
                  class="button-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M19 12H5"></path>
                  <path d="M12 19l-7-7 7-7"></path>
                </svg>
                <span>Back to Accounts</span>
              </button>
            </div>
          </div>
        </header>

        <!-- Main content -->
        <main class="inbox-content">
          <!-- Loading state -->
          <div v-if="loading" class="loading-state">
            <svg class="spinner" viewBox="0 0 50 50">
              <circle
                class="path"
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke-width="4"
              ></circle>
            </svg>
            <p class="loading-text">Loading conversations...</p>
          </div>

          <!-- Error state -->
          <div v-else-if="error" class="error-state">
            <div class="error-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            <p class="error-message">{{ error }}</p>
          </div>

          <!-- Conversation list -->
          <div v-else class="conversation-list-container">
            <ConversationList
              :conversations="conversations"
              :loading="refreshing"
              :totalItems="totalConversations"
              @conversation-click="openConversation"
              @delete-conversation="handleDeleteConversation"
              @mark-read="handleMarkConversationAsRead"
              @refresh="refreshConversations"
            />
          </div>

          <!-- Pagination -->
          <div
            v-if="!loading && totalConversations > 0"
            class="pagination-container"
          >
            <div class="pagination-info">
              Showing <span class="highlight">{{ startIndex + 1 }}</span> to
              <span class="highlight">{{ endIndex }}</span> of
              <span class="highlight">{{ totalConversations }}</span>
              conversations
            </div>

            <!-- Desktop pagination -->
            <div class="pagination-controls">
              <button
                @click="prevPage"
                :disabled="currentPage === 1"
                class="pagination-arrow"
                aria-label="Previous page"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>

              <div class="pagination-pages">
                <button
                  v-for="page in displayedPages"
                  :key="page"
                  @click="goToPage(page)"
                  :class="[
                    'pagination-page',
                    currentPage === page ? 'active' : '',
                    typeof page !== 'number' ? 'ellipsis' : '',
                  ]"
                >
                  {{ page }}
                </button>
              </div>

              <button
                @click="nextPage"
                :disabled="currentPage === totalPages"
                class="pagination-arrow"
                aria-label="Next page"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            </div>

            <!-- Mobile pagination -->
            <div class="mobile-pagination">
              <button
                @click="prevPage"
                :disabled="currentPage === 1"
                class="mobile-pagination-button"
              >
                Previous
              </button>
              <span class="mobile-pagination-info">
                Page {{ currentPage }} of {{ totalPages }}
              </span>
              <button
                @click="nextPage"
                :disabled="currentPage === totalPages"
                class="mobile-pagination-button"
              >
                Next
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import ConversationList from "@/components/ConversationList.vue";
import { connectWebSocket, disconnectWebSocket } from "@/services/websocket";

export default {
  name: "InboxView",
  components: {
    ConversationList,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const store = useStore();

    const emailId = computed(() => route.params.id);
    const currentEmail = ref(null);
    const conversations = ref([]);
    const loading = ref(true);
    const refreshing = ref(false);
    const error = ref("");
    const currentFolder = ref("inbox");
    const currentPage = ref(1);
    const itemsPerPage = ref(50);
    const totalConversations = ref(0);

    const pageTitle = computed(() => {
      if (!currentEmail.value) return "Inbox";
      return `${currentEmail.value.email}`;
    });

    // Pagination computed properties
    const totalPages = computed(() =>
      Math.max(1, Math.ceil(totalConversations.value / itemsPerPage.value))
    );

    const startIndex = computed(
      () => (currentPage.value - 1) * itemsPerPage.value
    );

    const endIndex = computed(() =>
      Math.min(currentPage.value * itemsPerPage.value, totalConversations.value)
    );

    const displayedPages = computed(() => {
      const pages = [];

      // Always show first page
      if (totalPages.value > 1) {
        pages.push(1);
      }

      // Add ellipsis if needed
      if (currentPage.value > 3) {
        pages.push("...");
      }

      // Add pages around current page
      for (
        let i = Math.max(2, currentPage.value - 1);
        i <= Math.min(totalPages.value - 1, currentPage.value + 1);
        i++
      ) {
        pages.push(i);
      }

      // Add ellipsis if needed
      if (currentPage.value < totalPages.value - 2) {
        pages.push("...");
      }

      // Always show last page if not already added
      if (totalPages.value > 1 && !pages.includes(totalPages.value)) {
        pages.push(totalPages.value);
      }

      return pages;
    });

    const fetchEmailDetails = async () => {
      try {
        loading.value = true;
        const response = await store.dispatch(
          "fetchEmailDetails",
          emailId.value
        );
        currentEmail.value = response;
      } catch (err) {
        error.value = err.message || "Failed to load email details";
      } finally {
        loading.value = false;
      }
    };

    const fetchConversations = async () => {
      try {
        refreshing.value = true;
        const response = await store.dispatch("fetchConversationsForEmail", {
          emailId: emailId.value,
          folder: currentFolder.value,
          page: currentPage.value,
          page_size: itemsPerPage.value,
        });

        console.log("API Response:", response);
        console.log("Conversations count:", response.count);
        console.log("Conversations results length:", response.results?.length);
        console.log("Current page:", currentPage.value);
        console.log("Items per page:", itemsPerPage.value);

        // Handle pagination data from API response
        conversations.value = response.results || [];
        totalConversations.value = response.count || 0;

        console.log("Total conversations set to:", totalConversations.value);
      } catch (err) {
        error.value = err.message || "Failed to load conversations";
      } finally {
        refreshing.value = false;
      }
    };

    const refreshConversations = () => {
      fetchConversations();
    };

    // Pagination methods
    const goToPage = (page) => {
      if (typeof page === "number" && page !== currentPage.value) {
        currentPage.value = page;
        fetchConversations();
      }
    };

    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        currentPage.value++;
        fetchConversations();
      }
    };

    const prevPage = () => {
      if (currentPage.value > 1) {
        currentPage.value--;
        fetchConversations();
      }
    };

    const openConversation = (conversationId) => {
      router.push({
        name: "ConversationView",
        params: {
          conversationId: conversationId,
        },
        query: {
          from: "inbox",
          emailId: emailId.value,
        },
      });
    };

    const goBack = () => {
      router.push({
        name: "Dashboard",
        query: {
          tab: "emails",
        },
      });
    };

    const handleNewMessage = (message) => {
      try {
        console.log("WebSocket received message:", message);

        if (!message) {
          console.error("Received empty message from WebSocket");
          return;
        }

        // Handle the new format where message is wrapped in a message property
        const messageData =
          message.type === "new_email" ? message.data : message;
        console.log("Processed message data:", messageData);
        console.log(
          "Current emailId:",
          emailId.value,
          "Current folder:",
          currentFolder.value
        );

        if (!messageData || !messageData.folder) {
          console.error("Received invalid message data:", messageData);
          return;
        }

        // Check if message belongs to current email and folder
        if (
          (messageData.email_address_id === parseInt(emailId.value) ||
            messageData.email_address === parseInt(emailId.value)) &&
          messageData.folder === currentFolder.value
        ) {
          console.log("Message matched current view, refreshing conversations");
          // Refresh conversations to get updated data
          fetchConversations();
        }
      } catch (error) {
        console.error("Error handling WebSocket message:", error);
      }
    };

    const handleDeleteConversation = async (conversationId) => {
      try {
        await store.dispatch("deleteConversation", conversationId);

        // Remove the conversation from local state to provide immediate feedback
        const conversationIndex = conversations.value.findIndex(
          (conv) => conv.id === conversationId
        );
        if (conversationIndex !== -1) {
          conversations.value.splice(conversationIndex, 1);
          totalConversations.value--;
        }

        // Refresh conversations to get updated data
        fetchConversations();
      } catch (err) {
        console.error("Failed to delete conversation:", err);
        error.value = err.message || "Failed to delete conversation";
      }
    };

    const handleMarkConversationAsRead = async (conversationId) => {
      try {
        await store.dispatch("markConversationAsRead", conversationId);

        // Update the conversation in local state
        const conversationIndex = conversations.value.findIndex(
          (conv) => conv.id === conversationId
        );
        if (conversationIndex !== -1) {
          conversations.value[conversationIndex].unread_count = 0;
        }
      } catch (err) {
        console.error("Failed to mark conversation as read:", err);
        error.value = err.message || "Failed to mark conversation as read";
      }
    };

    // When folder changes, fetch new conversations
    watch(currentFolder, () => {
      currentPage.value = 1; // Reset to first page when changing folders
      fetchConversations();
    });

    onMounted(async () => {
      await fetchEmailDetails();
      await fetchConversations();

      // Connect to WebSocket for this email inbox
      connectWebSocket(`mailbox/${emailId.value}/`, handleNewMessage);
    });

    onBeforeUnmount(() => {
      disconnectWebSocket();
    });

    return {
      currentEmail,
      conversations,
      loading,
      refreshing,
      error,
      currentFolder,
      currentPage,
      itemsPerPage,
      totalConversations,
      pageTitle,
      openConversation,
      goBack,
      refreshConversations,
      handleDeleteConversation,
      handleMarkConversationAsRead,
      totalPages,
      startIndex,
      endIndex,
      displayedPages,
      goToPage,
      nextPage,
      prevPage,
    };
  },
};
</script>

<style lang="scss">
// Variables
$primary-color: #3b82f6;
$primary-hover: #2563eb;
$primary-light: #eff6ff;
$success-color: #10b981;
$error-color: #ef4444;
$error-light: #fee2e2;
$error-dark: #b91c1c;

$text-primary: #111827;
$text-secondary: #4b5563;
$text-light: #9ca3af;
$text-white: #ffffff;

$border-color: #e5e7eb;
$border-dark: #d1d5db;

$bg-white: #ffffff;
$bg-light: #f9fafb;
$bg-gray: #f3f4f6;
$bg-hover: #e5e7eb;

$shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.05);
$shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
$shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
  0 2px 4px -1px rgba(0, 0, 0, 0.06);
$shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
  0 4px 6px -2px rgba(0, 0, 0, 0.05);

$radius-sm: 0.25rem;
$radius: 0.375rem;
$radius-md: 0.5rem;
$radius-lg: 0.75rem;
$radius-full: 9999px;

$transition: all 0.2s ease;

// Base container
.inbox-container {
  width: 100%;
  height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
  color: $text-primary;
  background-color: $bg-gray;
  overflow: hidden;
}

// Main layout
.inbox-layout {
  display: flex;
  flex-direction: column;
  height: 100%;
}

// Main content area
.inbox-main {
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  overflow: hidden;
}

// Header styling
.inbox-header {
  background-color: $bg-white;
  border-bottom: 1px solid $border-color;
  box-shadow: $shadow;
  z-index: 10;
  padding: 0;

  .header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.25rem 1.5rem;
    max-width: 1280px;
    margin: 0 auto;
    width: 100%;

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
      padding: 1rem;
    }
  }

  .header-info {
    display: flex;
    flex-direction: column;

    .header-title {
      font-size: 1.5rem;
      font-weight: 700;
      color: $text-primary;
      margin: 0;
      line-height: 1.2;
    }

    .header-subtitle {
      font-size: 0.875rem;
      color: $text-secondary;
      margin: 0.25rem 0 0 0;
      display: flex;
      align-items: center;

      .message-count {
        font-weight: 600;
        margin-right: 0.25rem;
      }
    }
  }

  .header-actions {
    display: flex;
    gap: 0.75rem;

    @media (max-width: 640px) {
      width: 100%;
      justify-content: space-between;
    }
  }
}

// Action button styling
.action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: $radius;
  transition: $transition;
  cursor: pointer;

  .button-icon {
    width: 1.125rem;
    height: 1.125rem;
    stroke-width: 2;
    flex-shrink: 0;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &.refresh-button {
    color: $text-secondary;
    background-color: $bg-white;
    border: 1px solid $border-color;

    &:hover:not(:disabled) {
      background-color: $bg-light;
      border-color: $border-dark;
    }

    &:focus {
      box-shadow: 0 0 0 3px rgba($primary-color, 0.15);
      border-color: $primary-color;
    }

    &.is-refreshing {
      .button-icon {
        animation: spin 1.2s linear infinite;
      }
    }
  }

  &.back-button {
    color: $text-white;
    background-color: $primary-color;
    border: none;

    &:hover {
      background-color: $primary-hover;
    }

    &:focus {
      box-shadow: 0 0 0 3px rgba($primary-color, 0.3);
    }
  }
}

// Main content
.inbox-content {
  flex: 1;
  overflow: auto;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  max-width: 1280px;
  margin: 0 auto;
  width: 100%;
  min-height: 0; /* Important for flex containers */

  @media (max-width: 768px) {
    padding: 1rem;
  }
}

// Loading state
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  background-color: $bg-white;
  border-radius: $radius-md;
  box-shadow: $shadow-md;
  margin: 2rem auto;
  max-width: 24rem;

  .spinner {
    width: 3.5rem;
    height: 3.5rem;
    animation: rotate 2s linear infinite;
    margin-bottom: 1.5rem;

    .path {
      stroke: $primary-color;
      stroke-linecap: round;
      animation: dash 1.5s ease-in-out infinite;
    }
  }

  .loading-text {
    color: $text-secondary;
    font-size: 1rem;
    margin: 0;
  }
}

// Error state
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: $error-light;
  border-radius: $radius-md;
  padding: 1.25rem;
  margin-bottom: 1.5rem;

  .error-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    background-color: rgba($error-color, 0.1);
    border-radius: $radius-full;
    color: $error-color;
    margin-bottom: 0.75rem;

    svg {
      width: 1.5rem;
      height: 1.5rem;
    }
  }

  .error-message {
    color: $error-dark;
    font-size: 0.875rem;
    text-align: center;
    margin: 0;
  }
}

// Conversation list container
.conversation-list-container {
  width: 100%;
  background-color: $bg-white;
  border-radius: $radius-md;
  box-shadow: $shadow-md;
  overflow: visible;
  display: flex;
  flex-direction: column;
  min-height: 400px;
}

// Animations
@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

// Pagination
.pagination-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background-color: $bg-white;
  border-radius: $radius-md;
  box-shadow: $shadow-md;
  margin-top: 1.5rem;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
}

.pagination-info {
  font-size: 0.875rem;
  color: $text-secondary;

  .highlight {
    font-weight: 500;
    color: $text-primary;
  }
}

.pagination-controls {
  display: none;
  align-items: center;
  gap: 0.25rem;

  @media (min-width: 768px) {
    display: flex;
  }
}

.pagination-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  padding: 0;
  background-color: $bg-white;
  border: 1px solid $border-color;
  border-radius: $radius;
  cursor: pointer;
  transition: $transition;

  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: $text-secondary;
  }

  &:hover:not(:disabled) {
    background-color: $bg-hover;
    border-color: $primary-color;
    color: $primary-color;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.pagination-pages {
  display: flex;
  gap: 0.25rem;
}

.pagination-page {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 2.25rem;
  height: 2.25rem;
  padding: 0 0.5rem;
  font-size: 0.875rem;
  color: $text-secondary;
  background-color: $bg-white;
  border: 1px solid $border-color;
  border-radius: $radius;
  cursor: pointer;
  transition: $transition;

  &:hover:not(.active):not(.ellipsis) {
    background-color: $bg-hover;
    border-color: $primary-color;
    color: $primary-color;
  }

  &.active {
    color: $text-white;
    background-color: $primary-color;
    border-color: $primary-color;
  }

  &.ellipsis {
    cursor: default;
  }
}

.mobile-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  @media (min-width: 768px) {
    display: none;
  }

  .mobile-pagination-button {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    color: $text-secondary;
    background-color: $bg-white;
    border: 1px solid $border-color;
    border-radius: $radius;
    cursor: pointer;
    transition: $transition;

    &:hover:not(:disabled) {
      background-color: $bg-hover;
      border-color: $primary-color;
      color: $primary-color;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .mobile-pagination-info {
    font-size: 0.875rem;
    color: $text-secondary;
  }
}
</style>
